<template>
  <div>
    <div v-if="!organization.isBroker && !organization.brokerApproved && !organization.brokerSigned">
      <h2 class="title">
        {{ $t("brokerRegistration") }}
      </h2>

      <b-message type="is-primary">
        <span v-html="$t('transExpPositives')" />
      </b-message>
      <b-message type="is-primary">
        {{ $t("qondaPartnerDesc1") }}
        <br /><br />
        {{ $t("qondaPartnerDesc2") }}
      </b-message>
      <div class="column mb-3">
        <div style="height: 315px; display: table-cell">
          <video width="560" height="315px" controls>
            <source :src="PARTNER_VIDEO_LINK" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
      <b-message type="is-primary">
        {{ $t("whyQondaPartner") }}
      </b-message>
      <b-field :label="$t('qondaPartnerRequirements')">
        <div>
          <div v-for="(f, i) in applicationFiles" :key="i" class="mb-3">
            <a :href="f.url" target="_blank">
              <b-button type="is-primary is-light">
                {{ f.name }}
              </b-button>
            </a>
          </div>
          <UploadFile
            @fileUploaded="
              (f) => {
                applicationFiles.push(f);
              }
            "
          />
        </div>
      </b-field>
      <b-field :label="$t('partnerApplicationReason')">
        <b-input v-model="applicationText" maxlength="4000" type="textarea" :loading="loading" :disabled="loading" />
      </b-field>
      <b-field :label="$t('yourHomepage') + ' (' + $t('optional') + ')'">
        <b-input v-model="link" :loading="loading" :disabled="disabled" />
      </b-field>
      <b-button
        class="is-primary mt-1"
        :loading="loading"
        :disabled="loading || applicationFiles.length == 0"
        @click="sendApplication"
      >
        {{ $t("registerAsBroker") }}
      </b-button>
    </div>
    <div v-else-if="organization.isBroker && !organization.brokerApproved && !organization.brokerSigned">
      <h3 class="title">
        {{ $t("partnerApplicationSent") }}
      </h3>
      <b-message type="is-primary">
        {{ $t("brokerWaitExplained") }}
      </b-message>
      <div v-for="(a, i) in applications" :key="i">
        <b-field :label="$t('partnerApplicationReason')">
          <b-input v-model="a.applicationText" maxlength="4000" type="textarea" :disabled="true" />
        </b-field>
        <div v-for="(f, k) in a.applicationFiles" :key="k" class="mb-3">
          <a :href="f.url" target="_blank">
            <b-button type="is-primary is-light">
              {{ f.name }}
            </b-button>
          </a>
        </div>
      </div>
      <div class="mt-6">
        <div style="height: 315px; display: table-cell">
          <video width="560" height="315px" controls>
            <source :src="PARTNER_VIDEO_LINK" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </div>
    <div v-else-if="organization.isBroker && organization.brokerApproved && !organization.brokerSigned">
      <h3 class="title">
        {{ $t("signDocs") }}
      </h3>
      <b-message type="is-primary">
        {{ $t("brokerSignDocsExplained") }}
      </b-message>
      <div v-if="pdfUrl" class="pdf-container">
        <PDFViewer :src="pdfUrl" :blob="pdfBlob" :loading="loading" />
        <br />
        <PDFViewer :src="$t('termsOfSalePDF')" :loading="loading" />
        <br />
        <b-field :label="$t('signDocHere')">
          <Signatur @signed="signDoc" />
        </b-field>
      </div>
    </div>
    <div v-else-if="organization.isBroker && organization.brokerApproved && organization.brokerSigned">
      <h3 class="title">
        {{ $t("affiliateTools") }}
      </h3>
      <AffiliateTools :referral-id="$store.state.organizationData.brokerRefCode" />
      <h4 class="subtitle mt-6">
        {{ $t("refOrgas") }}
      </h4>
      <div class="content mt-1">
        <ul>
          <li v-for="(org, i) in referredByOrg" :key="i">
            {{ org.legalName }}<br />
            <span v-if="org.created"
              >{{ $t("registered") }}: {{ new Date(org.created._seconds * 1000).toLocaleDateString("de-DE") }}</span
            >
          </li>
        </ul>
      </div>
      <h4 class="subtitle mt-6">
        {{ $t("refTrans") }}
      </h4>
      <div class="content mt-1">
        <ul>
          <li v-for="(trans, i) in referredTransByOrg" :key="i">
            {{ trans.firstName }} {{ trans.lastName }}<br />
            {{ $t("lastLogin") }}: {{ new Date(trans.lastLogin).toLocaleDateString("de-DE") }}<br />
            {{ $t("registered") }}:
            {{ new Date(trans.creationTime).toLocaleDateString("de-DE") }}
          </li>
        </ul>
      </div>
      <div class="mt-6" />
    </div>
  </div>
</template>

<script>
import UploadFile from "./UploadFile";
import { db, functions } from "@/firebase";
import AffiliateTools from "./AffiliateTools";
import pdfform from "pdfform.js/pdfform";
const PDFViewer = () => import("./PDFViewer");
const Signatur = () => import("./Signatur");
import { PARTNER_VIDEO_LINK } from "@/env";

export default {
  name: "PartnerTab",
  components: {
    UploadFile,
    PDFViewer,
    Signatur,
    AffiliateTools,
  },
  data() {
    return {
      applicationFiles: [],
      applicationText: "",
      link: "",
      loading: false,
      applications: [],
      pdfPage: 1,
      pdfMaxPage: 1,
      referredByOrg: [],
      referredTransByOrg: [],
      pdfUrl: null,
      pdfBlob: null,
      PARTNER_VIDEO_LINK,
    };
  },
  computed: {
    organization() {
      return this.$store.state.organizationData;
    },
  },
  async mounted() {
    window.pdfform = pdfform;
    if (this.organization.isBroker && this.organization.brokerApproved && !this.organization.brokerSigned) {
      var xhr = new XMLHttpRequest();
      let url = this.$i18n.t("internationalSalesContractPDF");
      xhr.open("GET", url, true);
      xhr.responseType = "arraybuffer";
      var vm = this;

      const blobToBase64 = (blob) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        return new Promise((resolve) => {
          reader.onloadend = () => {
            resolve(reader.result);
          };
        });
      };

      xhr.onload = async function () {
        window.theDoc = this.response;
        if (this.status == 200) {
          console.log(pdfform().list_fields(this.response));
          let user_data = (
            await db.collection("user_information").doc(vm.$store.getters.getCurrentUser.uid).get()
          ).data();
          let out_buf = pdfform().transform(this.response, {
            Unternehmen: [vm.organization.legalName],
            "Adresse und Land": [
              vm.organization.legalStreetName + " " + vm.organization.legalZipCode + " " + vm.organization.legalCity,
            ],
            Name: [user_data.firstName + " " + user_data.lastName],
            Steuernummer: [vm.organization.vatNo ? vm.organization.vatNo : ""],
            Datum: [new Date().toLocaleDateString()],
            Datum2: [new Date().toLocaleDateString()],
          });
          var blob = new Blob([out_buf], { type: "application/pdf" });
          vm.pdfBlob = blob;
          vm.pdfUrl = await blobToBase64(blob);
        } else {
          console.error("failed to load URL (code: " + this.status + ")");
        }
      };

      xhr.send();
    }
    if (this.organization.isBroker && this.organization.brokerApproved && this.organization.brokerSigned) {
      functions
        .httpsCallable("ShowReferredOrganizations")({
          organizationId: this.$store.getters.getOrganizationId,
        })
        .then(({ data }) => {
          this.referredByOrg = data.data;
        });
      functions
        .httpsCallable("ShowReferredTranslators")({
          organizationId: this.$store.getters.getOrganizationId,
        })
        .then(({ data }) => {
          this.referredTransByOrg = data.data;
        });
    }
  },
  methods: {
    async signDoc(signature) {
      this.loading = true;
      try {
        await functions.httpsCallable("SignPartnerDoc")({
          organizationId: this.$store.getters.getOrganizationId,
          signature: signature,
        });
        this.$store.state.organizationData.isBroker = true;
        this.$buefy.toast.open({
          message: this.$t("registrationSucceed"),
          type: "is-success",
        });
        window.location.reload();
      } catch (err) {
        this.$buefy.toast.open({
          message: this.$t("registrationFailed"),
          type: "is-danger",
        });
      }
      this.loading = false;
    },
    async sendApplication() {
      this.loading = true;
      try {
        await functions.httpsCallable("ApplyAsPartner")({
          organizationId: this.$store.getters.getOrganizationId,
          applicationFiles: this.applicationFiles,
          applicationText: this.applicationText,
          homepageLink: this.link,
        });
        this.$store.state.organizationData.isBroker = true;
        this.$buefy.toast.open({
          message: this.$t("partnerApplicationSent"),
          type: "is-success",
        });
        window.location.reload();
      } catch (err) {
        this.$buefy.toast.open({
          message: this.$t("registrationFailed"),
          type: "is-danger",
        });
      }
      this.loading = false;
    },
  },
  firestore() {
    return {
      applications: db
        .collection("organizations")
        .doc(this.$store.getters.getOrganizationId)
        .collection("applications")
        .orderBy("timestamp", "desc")
        .limit(1),
    };
  },
};
</script>

<style>
.pdf-container {
  max-width: 540px;
  margin: auto;
}
</style>
